import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { useForm, useFieldArray, Controller, Control, UseFormWatch, FieldValues, Path, ArrayPath } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Flag as FlagIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  AccountBalance as BankIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { ECountryCode, ListAccountingAreasQuery } from '__generated__/graphql';
import { LIST_ACCOUNTING_AREAS, UPDATE_ACCOUNTING_AREAS } from '../pages/clients/gql';
import { dispatchMessage } from 'helper/snackbar';
import { FormInputText } from './form/FormInputText';
import { FormInputCountryDropdown } from './form/FormInputDropdown';
import yup from 'validation';
import * as _ from 'lodash';

// Types
interface EmailRecipient {
  id?: number;
  email: string;
  name?: string | null;
}

interface AccountingAreaFormType {
  dbId?: number;
  name: string;
  attentionName?: string | null;
  vatId?: string | null;
  billingCountry: ECountryCode;
  billingZip: string;
  billingCity: string;
  billingCompany?: string | null;
  billingStreet?: string | null;
  billingAddressAddition?: string | null;
  invoiceEmail?: string | null;
  isDefault: boolean;
  additionalEmailRecipients: EmailRecipient[];
}

// Build schema from current language to keep validation labels/messages in sync.
const createAccountingAreaSchema = () =>
  yup.object().shape({
    name: yup.string().required().label(i18next.t('accounting-area-company-name-required')).min(2),
    attentionName: yup.string().nullable(),
    vatId: yup
      .string()
      .required()
      .label(i18next.t('accounting-area-vat-id-required'))
      .test('vatId-format', String(i18next.t('accounting-area-vat-id-invalid')), function (value) {
        if (!value || value.length === 0) return false; // Required field
        return /^[A-Z]{2}[0-9A-Z]+$/.test(value);
      }),
    billingCountry: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label(i18next.t('accounting-area-country-required')),
    billingZip: yup.string().required().label(i18next.t('accounting-area-zip-required')).min(4),
    billingCity: yup.string().required().label(i18next.t('accounting-area-city-required')).min(2),
    billingCompany: yup.string().nullable(),
    billingStreet: yup.string().required().label(i18next.t('accounting-area-street-required')).min(1),
    billingAddressAddition: yup.string().nullable(),
    invoiceEmail: yup.string().required().label(i18next.t('accounting-area-invoice-email-required')).email(),
    isDefault: yup.boolean().required(),
    additionalEmailRecipients: yup
      .array()
      .of(
        yup.object().shape({
          email: yup.string().email().required().label(i18next.t('email')),
        }),
      )
      .required(),
  });

// Email Recipients Manager Component
const EmailRecipientsManager = <
  T extends FieldValues & { additionalEmailRecipients: EmailRecipient[] },
  TName extends ArrayPath<T>
>({
  control,
  name,
  watch,
}: {
  control: Control<T>;
  name: TName;
  watch?: UseFormWatch<T>;
}) => {
  const { fields, append, remove } = useFieldArray<T, TName>({
    control,
    name,
  });

  const addRecipient = () => {
    // Cast is safe here because T is constrained to have additionalEmailRecipients: EmailRecipient[]
    append({ email: '' } as any);
  };

  return (
    <Box>
      {fields.map((field, index) => (
        <Grid container spacing={2} key={field.id} sx={{ mb: 2 }}>
          <Grid item xs={10}>
            <Controller
              name={`additionalEmailRecipients.${index}.email` as Path<T>}
              control={control}
              render={({ field: fieldProps, fieldState }) => (
                <TextField
                  {...fieldProps}
                  label={String(i18next.t('email'))}
                  fullWidth
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              onClick={() => remove(index)}
              color="error"
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button
        type="button"
        onClick={addRecipient}
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
      >
        {i18next.t('accounting-area-email-recipient-add')}
      </Button>
    </Box>
  );
};

// Country helper functions
const getCountryFlag = (country: ECountryCode): string => {
  const flags: Partial<Record<ECountryCode, string>> = {
    [ECountryCode.DE]: '🇩🇪',
    [ECountryCode.AT]: '🇦🇹',
    [ECountryCode.CH]: '🇨🇭',
    [ECountryCode.FR]: '🇫🇷',
    [ECountryCode.IT]: '🇮🇹',
    [ECountryCode.NL]: '🇳🇱',
    [ECountryCode.BE]: '🇧🇪',
    [ECountryCode.ES]: '🇪🇸',
  };
  return flags[country] || '🌍';
};

const getCountryName = (country: ECountryCode): string => {
  const displayNames = new Intl.DisplayNames([i18next.language], { type: 'region' });
  return displayNames.of(country) || country;
};

interface AccountingArea {
  id: number;
  name: string;
  contactName?: string | null;
  bookingName?: string | null;
  attentionName?: string | null;
  invoiceEmail?: string | null;
  billingCompany?: string | null;
  billingStreet?: string | null;
  billingAddressAddition?: string | null;
  billingZip?: string | null;
  billingCity?: string | null;
  billingCountry: ECountryCode;
  billingComment?: string | null;
  vatId?: string | null;
  noVatIdReason?: string | null;
  commercialRegisterNumber?: string | null;
  taxId?: string | null;
  poId?: string | null;
  bankName?: string | null;
  bankCode?: string | null;
  iban?: string | null;
  bic?: string | null;
  isDefault: boolean;
  additionalEmailRecipients: EmailRecipient[];
}

interface AccountingAreasComponentProps {
  accountingAreas: AccountingArea[];
  publicCompanyId: number;
}

/**
 * Component for managing accounting areas (Buchungskreise) for a company.
 * Supports CRUD operations with validation, default area management, and additional email recipients.
 */
const AccountingAreasComponent: React.FC<AccountingAreasComponentProps> = ({ accountingAreas, publicCompanyId }) => {
  const dispatch = useDispatch();
  const accountingAreaSchema = React.useMemo(() => createAccountingAreaSchema(), [i18next.language]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [editingArea, setEditingArea] = useState<AccountingAreaFormType | null>(null);

  const [updateAccountingAreasMutation] = useMutation(UPDATE_ACCOUNTING_AREAS);

  const {
    handleSubmit: handleEditSubmit,
    control: editControl,
    reset: editReset,
    watch: editWatch,
    formState: { isSubmitting: editIsSubmitting }
  } = useForm<AccountingAreaFormType>({
    mode: 'onChange',
    // Cast to any to satisfy react-hook-form's resolver typing with yup
    resolver: yupResolver(accountingAreaSchema) as any,
  });

  const openEditDialog = (area: AccountingArea) => {
    const formData: AccountingAreaFormType = {
      dbId: area.id,
      name: area.name || '',
      attentionName: area.attentionName || null,
      vatId: area.vatId || null,
      billingCountry: area.billingCountry,
      billingZip: area.billingZip || '',
      billingCity: area.billingCity || '',
      billingCompany: area.billingCompany || null,
      billingStreet: area.billingStreet || null,
      billingAddressAddition: area.billingAddressAddition || null,
      invoiceEmail: area.invoiceEmail || null,
      isDefault: area.isDefault,
      additionalEmailRecipients: area.additionalEmailRecipients || [],
    };
    setEditingArea(formData);
    editReset(formData);
    setShowEditDialog(true);
  };

  const openNewDialog = () => {
    const newArea: AccountingAreaFormType = {
      name: '',
      attentionName: null,
      vatId: null,
      billingCountry: ECountryCode.DE,
      billingZip: '',
      billingCity: '',
      billingCompany: null,
      billingStreet: null,
      billingAddressAddition: null,
      invoiceEmail: null,
      isDefault: accountingAreas.length === 0, // First one is default
      additionalEmailRecipients: [],
    };
    setEditingArea(newArea);
    editReset(newArea);
    setShowNewDialog(true);
  };

  const onEditSubmit = async (values: AccountingAreaFormType) => {
    try {
      const isNewArea = !values.dbId;
      // Create a shallow-cloned list to avoid mutating Apollo-frozen objects
      let tempId: number | null = null;
      let updatedAreas: AccountingArea[] = [];

      if (isNewArea) {
        tempId = Date.now();
        updatedAreas = [
          ...accountingAreas.map(a => ({ ...a })),
          { ...(values as any), id: tempId },
        ];
      } else {
        updatedAreas = accountingAreas.map(area =>
          area.id === values.dbId ? { ...area, ...(values as any), id: values.dbId! } : { ...area }
        );
      }

      // If setting this area as default, ensure others are not default immutably
      if (values.isDefault) {
        const defaultId = isNewArea ? (tempId as number) : (values.dbId as number);
        updatedAreas = updatedAreas.map(area => ({
          ...area,
          isDefault: area.id === defaultId,
        }));
      }

      await updateAccountingAreasMutation({
        variables: {
          publicCompanyId: publicCompanyId,
          data: updatedAreas.map(area => ({
            id: tempId !== null && area.id === tempId ? undefined : area.id,
            name: area.name || '',
            vatId: area.vatId || null,
            commercialRegisterNumber: area.commercialRegisterNumber || null,
            bankName: area.bankName || null,
            bankCode: area.bankCode || null,
            iban: area.iban || null,
            bic: area.bic || null,
            billingCountry: area.billingCountry,
            billingZip: area.billingZip || '',
            billingCity: area.billingCity || '',
            billingCompany: area.billingCompany || area.name || '',
            billingStreet: area.billingStreet || null,
            billingAddressAddition: area.billingAddressAddition || null,
            invoiceEmail: area.invoiceEmail || null,
            isDefault: area.isDefault,
            additionalEmailRecipients: (area.additionalEmailRecipients || []).map((recipient: EmailRecipient) => ({
              id: recipient.id || undefined,
              email: recipient.email,
              name: recipient.name || null,
            })),
          })),
        },
        refetchQueries: [{ query: LIST_ACCOUNTING_AREAS, variables: { publicCompanyId } }],
      });

      dispatchMessage(dispatch, String(i18next.t(isNewArea ? 'accounting-area-created' : 'accounting-area-saved')));
      setShowEditDialog(false);
      setShowNewDialog(false);
      setEditingArea(null);
    } catch (err: unknown) {
      dispatchMessage(
        dispatch,
        String(
          i18next.t('accounting-area-save-error', {
            message: err instanceof Error ? err.message : String(err),
          }),
        ),
      );
    }
  };

  const setAsDefault = async (areaId: number) => {
    try {
      const updatedAreas = accountingAreas.map(area => ({
        ...area,
        isDefault: area.id === areaId,
      }));

      await updateAccountingAreasMutation({
        variables: {
          publicCompanyId: publicCompanyId,
          data: updatedAreas.map(area => ({
            id: area.id,
            name: area.name || '',
            vatId: area.vatId || null,
            commercialRegisterNumber: area.commercialRegisterNumber || null,
            bankName: area.bankName || null,
            bankCode: area.bankCode || null,
            iban: area.iban || null,
            bic: area.bic || null,
            billingCountry: area.billingCountry,
            billingZip: area.billingZip || '',
            billingCity: area.billingCity || '',
            billingCompany: area.billingCompany || area.name || '',
            billingStreet: area.billingStreet || null,
            billingAddressAddition: area.billingAddressAddition || null,
            invoiceEmail: area.invoiceEmail || null,
            isDefault: area.isDefault,
            additionalEmailRecipients: (area.additionalEmailRecipients || []).map((recipient: EmailRecipient) => ({
              id: recipient.id || undefined,
              email: recipient.email,
              name: recipient.name || null,
            })),
          })),
        },
        refetchQueries: [{ query: LIST_ACCOUNTING_AREAS, variables: { publicCompanyId } }],
      });

      dispatchMessage(dispatch, String(i18next.t('accounting-area-default-set')));
    } catch (err: unknown) {
      dispatchMessage(
        dispatch,
        String(i18next.t('accounting-area-default-error', { message: err instanceof Error ? err.message : String(err) })),
      );
    }
  };

  const deleteArea = async (areaId: number) => {
    const area = accountingAreas.find(a => a.id === areaId);
    if (area?.isDefault) {
      dispatchMessage(dispatch, String(i18next.t('accounting-area-cannot-delete-default')));
      return;
    }

    if (!window.confirm(String(i18next.t('accounting-area-delete-confirm')))) return;

    try {
      const updatedAreas = accountingAreas.filter(area => area.id !== areaId);

      await updateAccountingAreasMutation({
        variables: {
          publicCompanyId: publicCompanyId,
          data: updatedAreas.map(area => ({
            id: area.id,
            name: area.name || '',
            vatId: area.vatId || null,
            commercialRegisterNumber: area.commercialRegisterNumber || null,
            bankName: area.bankName || null,
            bankCode: area.bankCode || null,
            iban: area.iban || null,
            bic: area.bic || null,
            billingCountry: area.billingCountry,
            billingZip: area.billingZip || '',
            billingCity: area.billingCity || '',
            billingCompany: area.billingCompany || area.name || '',
            billingStreet: area.billingStreet || null,
            billingAddressAddition: area.billingAddressAddition || null,
            invoiceEmail: area.invoiceEmail || null,
            isDefault: area.isDefault,
            additionalEmailRecipients: (area.additionalEmailRecipients || []).map((recipient: EmailRecipient) => ({
              id: recipient.id || undefined,
              email: recipient.email,
              name: recipient.name || null,
            })),
          })),
        },
        refetchQueries: [{ query: LIST_ACCOUNTING_AREAS, variables: { publicCompanyId } }],
      });

      dispatchMessage(dispatch, String(i18next.t('accounting-area-deleted')));
    } catch (err: unknown) {
      dispatchMessage(
        dispatch,
        String(
          i18next.t('accounting-area-delete-error', {
            message: err instanceof Error ? err.message : String(err),
          }),
        ),
      );
    }
  };

  return (
    <Box>
      {/* Cards Grid */}
      <Grid container spacing={3}>
        {accountingAreas.map((area) => (
          <Grid item xs={12} md={6} lg={4} key={area.id}>
            <Card sx={{ height: '100%', position: 'relative' }}>
              <CardContent sx={{ pb: 1 }}>
                {/* Header with company name */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    {area.name}
                  </Typography>
                  {!area.isDefault && (
                    <IconButton
                      onClick={() => deleteArea(area.id)}
                      color="error"
                      size="small"
                      title={String(i18next.t('delete'))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                {/* Country */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <Typography sx={{ mr: 1, fontSize: '1.2em' }}>{getCountryFlag(area.billingCountry)}</Typography>
                  <Typography variant="body2">{getCountryName(area.billingCountry)}</Typography>
                </Box>

                {/* Address */}
                <Box sx={{ mb: 2, color: 'text.secondary' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <LocationOnIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{area.billingZip} {area.billingCity}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{area.billingStreet}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{area.invoiceEmail}</Typography>
                  </Box>
                  {area.vatId && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FlagIcon sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">UID: {area.vatId}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>

              {/* Action buttons */}
              <Box sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => openEditDialog(area)}
                  size="small"
                  endIcon={<EditIcon />}
                >
                  Bearbeiten
                </Button>
                {area.isDefault ? (
                  <Chip
                    icon={<StarIcon />}
                    label={String(i18next.t('default'))}
                    size="small"
                    color="primary"
                    variant="filled"
                  />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setAsDefault(area.id)}
                    size="small"
                  >
                    {i18next.t('accounting-area-set-default')}
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add new button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNewDialog}
        >
          {i18next.t('accounting-area-new')}
        </Button>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={showEditDialog || showNewDialog}
        onClose={() => {
          setShowEditDialog(false);
          setShowNewDialog(false);
          setEditingArea(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingArea?.dbId ? i18next.t('accounting-area-edit') : i18next.t('accounting-area-new')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <form onSubmit={handleEditSubmit(onEditSubmit)}>
            <Grid container spacing={3}>
              {/* Grunddaten */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{i18next.t('accounting-area-basic-data')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="name"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={String(i18next.t('accounting-area-company-name-required'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="attentionName"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label={String(i18next.t('accounting-area-attention'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="vatId"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label={String(i18next.t('accounting-area-vat-id-required'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>


              {/* Adresse */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{i18next.t('accounting-area-address')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Controller
                      name="billingCountry"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <FormInputCountryDropdown
                          name="billingCountry"
                          control={editControl}
                          label={String(i18next.t('accounting-area-country-required'))}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="billingZip"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={String(i18next.t('accounting-area-zip-required'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="billingCity"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={String(i18next.t('accounting-area-city-required'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="billingStreet"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label={String(i18next.t('accounting-area-street-required'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="billingAddressAddition"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label={String(i18next.t('accounting-area-address-addition'))}
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="invoiceEmail"
                      control={editControl}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label={String(i18next.t('accounting-area-invoice-email-required'))}
                          fullWidth
                          type="email"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Email-Empfänger */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{i18next.t('accounting-area-email-recipients')}</Typography>
                <EmailRecipientsManager
                  control={editControl}
                  name={"additionalEmailRecipients"}
                  watch={editWatch}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditDialog(false);
              setShowNewDialog(false);
              setEditingArea(null);
            }}
          >
            {i18next.t('cancel')}
          </Button>
          <Button
            onClick={handleEditSubmit(onEditSubmit)}
            variant="contained"
            disabled={editIsSubmitting}
          >
            {editIsSubmitting ? i18next.t('saving') : i18next.t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingAreasComponent;
